import React, { useState, useCallback } from 'react';
import { useAppStore } from '../../store/appStore';
import { notesApi } from '../../services/api';

// Floating "add note" overlay anchored to the stream, next to the operator-chat
// overlay. Collapsed: a "＋ Note" launcher pill (sits above the Chat pill so the two
// don't overlap). Expanded: a small glass card with a text box, an "include snapshot"
// checkbox, and Submit. The note is timestamped server-side; history lives in the
// Controller Notes tab. onCreated() lets that tab refresh.
// `inset` places both the collapsed launcher and the open composer. See ChatPanel — the
// on-screen PTZ mode owns the bottom and right edges, and only the parent knows the mode.
export default function ControllerNotesOverlay({ cameraId, onCreated, inset = { bottom: '16px', left: '16px' } }) {
  const { addToast } = useAppStore();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [includeSnapshot, setIncludeSnapshot] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(async () => {
    const body = text.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    try {
      await notesApi.create(cameraId, { text: body, includeSnapshot });
      addToast({ type: 'success', message: includeSnapshot ? 'Note saved with snapshot' : 'Note saved' });
      setText('');
      setIncludeSnapshot(false);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to save note' });
    } finally {
      setSubmitting(false);
    }
  }, [text, includeSnapshot, submitting, cameraId, addToast, onCreated]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Add a controller note"
        style={{
          position: 'absolute', ...inset, zIndex: 25,
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 12px',
          background: 'rgba(8,12,18,0.88)',
          border: '1px solid var(--border-dim)',
          borderRadius: '999px',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          backdropFilter: 'blur(8px)',
          fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 700,
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
        </svg>
        Note
      </button>
    );
  }

  return (
    <div style={{
      position: 'absolute', ...inset, zIndex: 26,
      width: '320px', maxHeight: 'calc(100% - 32px)',
      display: 'flex', flexDirection: 'column',
      background: 'rgba(18,22,30,0.92)',
      border: '1px solid var(--border-dim)',
      borderRadius: 'var(--radius-md)',
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 12px', borderBottom: '1px solid var(--border-dim)', flexShrink: 0,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '7px',
          fontFamily: 'var(--font-display)', fontSize: '11px', fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-secondary)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent-cyan)" strokeWidth="2">
            <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
          Add Note
        </div>
        <button
          onClick={() => setOpen(false)}
          title="Close"
          style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px' }}
        >
          –
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <textarea
          className="input-field"
          autoFocus
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          placeholder="What did you see? (e.g. bear at the river with cubs)"
          maxLength={2000}
          rows={3}
          style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'var(--font-body)', fontSize: '13px', lineHeight: 1.4 }}
        />
        <label style={{
          display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
          fontFamily: 'var(--font-display)', fontSize: '12px', color: 'var(--text-secondary)',
        }}>
          <input
            type="checkbox"
            checked={includeSnapshot}
            onChange={e => setIncludeSnapshot(e.target.checked)}
            style={{ width: '15px', height: '15px', accentColor: 'var(--accent-cyan)', cursor: 'pointer' }}
          />
          Include snapshot
        </label>
        <button
          className="btn btn-primary"
          onClick={submit}
          disabled={!text.trim() || submitting}
          style={{ justifyContent: 'center' }}
        >
          {submitting ? 'Saving…' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
